import './tooltip.css'

import { Instance, createPopper } from '@popperjs/core'

import type { DirectiveBinding } from 'vue'

interface TooltipOptions {
  title: string
  placement?: 'top' | 'right' | 'bottom' | 'left'
  trigger?: 'hover' | 'click' | 'focus'
  disabled?: boolean
  delay?: {
    show: number
    hide: number
  }
  boundary?: 'viewport' | 'window' | 'scrollParent'
  arrow?: boolean
  arrowPadding?: number
  html?: boolean
  customClass?: string
  flipEnabled?: boolean
  interactive?: boolean
}

interface ExtendedHTMLElement extends HTMLElement {
  _tooltip?: HTMLElement
  _popper?: Instance
  _tooltipOptions?: TooltipOptions
  _showTimeout?: NodeJS.Timeout
  _hideTimeout?: NodeJS.Timeout
  _tooltipHoverTimeout?: NodeJS.Timeout
  _showTooltipHandler?: () => void
  _hideTooltipHandler?: () => void
  _clickOutsideHandler?: (e: MouseEvent) => void
  _originalTitle?: string
  _isHoveringElement?: boolean
  _isHoveringTooltip?: boolean
  _tooltipMouseEnterHandler?: () => void
  _tooltipMouseLeaveHandler?: () => void
}

const createTooltipElement = (title: string, html?: boolean, customClass?: string): HTMLElement => {
  const tooltip = document.createElement('div')
  tooltip.className = 'tooltip bs-tooltip-auto'
  tooltip.role = 'tooltip'
  if (customClass) {
    tooltip.classList.add(customClass)
  }
  const arrow = document.createElement('div')
  arrow.className = 'tooltip-arrow'
  
  const inner = document.createElement('div')
  inner.className = 'tooltip-inner'
  
  if (html) {
    inner.innerHTML = title
  } else {
    inner.textContent = title
  }
  
  tooltip.appendChild(arrow)
  tooltip.appendChild(inner)
  
  return tooltip
}

const showTooltip = (el: ExtendedHTMLElement) => {
  if (!el._tooltipOptions || el._tooltipOptions.disabled) return
  
  // Don't show tooltip if title is empty
  if (!el._tooltipOptions.title?.trim()) return
  
  // Clear any existing hide timeout
  if (el._hideTimeout) {
    clearTimeout(el._hideTimeout)
    el._hideTimeout = undefined
  }
  
  // Only use delay if explicitly specified and greater than 0
  if (el._tooltipOptions.delay?.show && el._tooltipOptions.delay.show > 0) {
    el._showTimeout = setTimeout(() => {
      createAndShowTooltip(el)
    }, el._tooltipOptions.delay.show)
  } else {
    createAndShowTooltip(el)
  }
}

const createAndShowTooltip = (el: ExtendedHTMLElement) => {
  if (!el._tooltipOptions) return
  
  // Don't create tooltip if title is empty
  if (!el._tooltipOptions.title?.trim()) return
  
  // Remove existing tooltip if any
  if (el._tooltip) {
    el._tooltip.remove()
    el._tooltip = undefined
  }
  
  if (el._popper) {
    el._popper.destroy()
    el._popper = undefined
  }
  
  // Create new tooltip
  const tooltip = createTooltipElement(el._tooltipOptions.title || '', el._tooltipOptions.html, el._tooltipOptions.customClass)
  document.body.appendChild(tooltip)
  el._tooltip = tooltip
  
  const flipOptions = el._tooltipOptions.flipEnabled
  ? { enabled: false, padding: 8 }
  : { fallbackPlacements: ['top', 'right', 'bottom', 'left'], padding: 8 };
  
  // Configure Popper
  el._popper = createPopper(el, tooltip, {
    placement: el._tooltipOptions.placement || 'top',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: tooltip.querySelector('.tooltip-arrow'),
          padding: 8,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: el._tooltipOptions.boundary === 'viewport' ? 'viewport' : 'clippingParents',
          padding: 8,
          altAxis: true,
        },
      },
      {
        name: 'flip',
        options: flipOptions,
      },
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false,
          adaptive: false,
        },
      },
    ],
  })
  
  requestAnimationFrame(() => {
    tooltip.classList.add('show')
    el._popper?.update()
    
    // Set up interactive hover listeners only if interactive option is enabled
    if (el._tooltipOptions?.interactive) {
      addTooltipHoverListeners(el)
    }
  })
}

const addTooltipHoverListeners = (el: ExtendedHTMLElement) => {
  if (!el._tooltip) return

  // Remove existing tooltip listeners using the dedicated cleanup function
  removeTooltipHoverListeners(el)

  // Store references for cleanup
  el._tooltipMouseEnterHandler = () => {
    el._isHoveringTooltip = true
    // Clear any pending hide timeout when hovering over tooltip
    if (el._hideTimeout) {
      clearTimeout(el._hideTimeout)
      el._hideTimeout = undefined
    }
    // Clear any pending tooltip hover timeout
    if (el._tooltipHoverTimeout) {
      clearTimeout(el._tooltipHoverTimeout)
      el._tooltipHoverTimeout = undefined
    }
  }

  el._tooltipMouseLeaveHandler = () => {
    el._isHoveringTooltip = false
    // Clear any existing tooltip hover timeout
    if (el._tooltipHoverTimeout) {
      clearTimeout(el._tooltipHoverTimeout)
    }
    // Check if we should hide when leaving tooltip
    el._tooltipHoverTimeout = setTimeout(() => {
      if (!el._isHoveringElement && !el._isHoveringTooltip) {
        hideTooltip(el)
      }
    }, 50)
  }
  
  el._tooltip.addEventListener('mouseenter', el._tooltipMouseEnterHandler)
  el._tooltip.addEventListener('mouseleave', el._tooltipMouseLeaveHandler)
}

const hideTooltip = (el: ExtendedHTMLElement) => {
  // Clear any pending show timeout
  if (el._showTimeout) {
    clearTimeout(el._showTimeout)
    el._showTimeout = undefined
  }
  
  const hide = () => {
    if (el._tooltip) {
      // Clean up tooltip hover listeners before removing the element
      removeTooltipHoverListeners(el)

      el._tooltip.classList.remove('show')
      // Remove the tooltip element from DOM after animation
      setTimeout(() => {
        if (el._tooltip) {
          el._tooltip.remove()
          el._tooltip = undefined
        }
        if (el._popper) {
          el._popper.destroy()
          el._popper = undefined
        }
      }, 150) // Match the CSS transition duration
    }
  }
  
  // Only use delay if explicitly specified and greater than 0
  if (el._tooltipOptions?.delay?.hide && el._tooltipOptions.delay.hide > 0) {
    el._hideTimeout = setTimeout(hide, el._tooltipOptions.delay.hide)
  } else {
    hide()
  }
}

const addTooltipListeners = (el: ExtendedHTMLElement) => {
  const trigger = el._tooltipOptions?.trigger || 'hover'
  
  // Remove any existing listeners first
  removeTooltipListeners(el)
  
  // Store handler references for cleanup
  el._showTooltipHandler = () => {
    // Temporarily remove title to prevent native tooltip
    if (el._originalTitle && el.getAttribute('title')) {
      el.removeAttribute('title')
    }
    if (el._tooltipOptions?.interactive) {
      el._isHoveringElement = true
    }
    showTooltip(el)
  }
  el._hideTooltipHandler = () => {
    // Restore title attribute when hiding tooltip
    if (el._originalTitle && !el.getAttribute('title')) {
      el.setAttribute('title', el._originalTitle)
    }
    if (el._tooltipOptions?.interactive) {
      // Interactive behavior: track hover state and add delay
      el._isHoveringElement = false
      if (el._hideTimeout) {
        clearTimeout(el._hideTimeout)
      }
      el._hideTimeout = setTimeout(() => {
        if (!el._isHoveringElement && !el._isHoveringTooltip) {
          hideTooltip(el)
        }
      }, 200) // 200ms delay to allow moving to tooltip
    } else {
      // Standard behavior: hide immediately
      hideTooltip(el)
    }
  }
  
  if (trigger === 'hover') {
    el.addEventListener('mouseenter', el._showTooltipHandler)
    el.addEventListener('mouseleave', el._hideTooltipHandler)
    // Also handle focus events for keyboard navigation
    el.addEventListener('focus', el._showTooltipHandler)
    el.addEventListener('blur', el._hideTooltipHandler)
  } else if (trigger === 'click') {
    el.addEventListener('click', el._showTooltipHandler)
    el._clickOutsideHandler = (e: MouseEvent) => {
      const target = e.target as Node
      if (!el.contains(target)) {
        hideTooltip(el)
      }
    }
    document.addEventListener('click', el._clickOutsideHandler)
  } else if (trigger === 'focus') {
    el.addEventListener('focus', el._showTooltipHandler)
    el.addEventListener('blur', el._hideTooltipHandler)
  }
}

const removeTooltipHoverListeners = (el: ExtendedHTMLElement) => {
  if (el._tooltip && el._tooltipMouseEnterHandler) {
    el._tooltip.removeEventListener('mouseenter', el._tooltipMouseEnterHandler)
  }
  if (el._tooltip && el._tooltipMouseLeaveHandler) {
    el._tooltip.removeEventListener('mouseleave', el._tooltipMouseLeaveHandler)
  }

  // Clear handler references to allow garbage collection
  el._tooltipMouseEnterHandler = undefined
  el._tooltipMouseLeaveHandler = undefined
  el._isHoveringTooltip = false
}

const removeTooltipListeners = (el: ExtendedHTMLElement) => {
  if (el._showTooltipHandler) {
    el.removeEventListener('mouseenter', el._showTooltipHandler)
    el.removeEventListener('click', el._showTooltipHandler)
    el.removeEventListener('focus', el._showTooltipHandler)
  }
  
  if (el._hideTooltipHandler) {
    el.removeEventListener('mouseleave', el._hideTooltipHandler)
    el.removeEventListener('blur', el._hideTooltipHandler)
  }
  
  if (el._clickOutsideHandler) {
    document.removeEventListener('click', el._clickOutsideHandler)
  }
  
  // Clear any pending timeouts
  if (el._showTimeout) {
    clearTimeout(el._showTimeout)
    el._showTimeout = undefined
  }
  if (el._hideTimeout) {
    clearTimeout(el._hideTimeout)
    el._hideTimeout = undefined
  }
  if (el._tooltipHoverTimeout) {
    clearTimeout(el._tooltipHoverTimeout)
    el._tooltipHoverTimeout = undefined
  }

  // Always clean up tooltip hover listeners if they exist, regardless of current interactive state
  removeTooltipHoverListeners(el)

  // Ensure tooltip is hidden and destroyed
  if (el._tooltip) {
    el._tooltip.remove()
    el._tooltip = undefined
  }
  if (el._popper) {
    el._popper.destroy()
    el._popper = undefined
  }
}

export const vTooltip = {
  mounted(el: ExtendedHTMLElement, binding: DirectiveBinding<TooltipOptions | string>) {
    el._originalTitle = el.getAttribute('title') || ''
    // Get placement, html, and trigger from modifiers
    const placement = Object.keys(binding.modifiers).find(mod => 
      ['top', 'right', 'bottom', 'left'].includes(mod)
    ) as 'top' | 'right' | 'bottom' | 'left' | undefined
    
    const html = binding.modifiers.html || false
    
    // Get trigger from modifiers, defaulting to hover
    const trigger = Object.keys(binding.modifiers).find(mod => 
      ['click', 'hover', 'focus'].includes(mod)
    ) as 'click' | 'hover' | 'focus' || 'hover'
    
    // Use explicit value if provided, otherwise use stored title
    if (binding.value !== undefined && binding.value !== null) {
      el._tooltipOptions = typeof binding.value === 'string' 
        ? { title: binding.value, placement, html, trigger }
        : { 
            ...binding.value, 
            placement: placement || binding.value.placement,
            html: html || binding.value.html,
            trigger: trigger || binding.value.trigger || 'hover'
          }
    } else {
      el._tooltipOptions = { 
        title: el._originalTitle || '',
        placement,
        html,
        trigger
      }
    }
    
    addTooltipListeners(el)
  },
  
  updated(el: ExtendedHTMLElement, binding: DirectiveBinding<TooltipOptions | string>) {
    const currentTitle = el.getAttribute('title')
    if (currentTitle) {
      el._originalTitle = currentTitle
    }
    
    // Get placement, html, and trigger from modifiers
    const placement = Object.keys(binding.modifiers).find(mod => 
      ['top', 'right', 'bottom', 'left'].includes(mod)
    ) as 'top' | 'right' | 'bottom' | 'left' | undefined
    
    const html = binding.modifiers.html || false
    
    // Get trigger from modifiers, defaulting to hover
    const trigger = Object.keys(binding.modifiers).find(mod => 
      ['click', 'hover', 'focus'].includes(mod)
    ) as 'click' | 'hover' | 'focus' || 'hover'

    // Store previous interactive state to detect changes
    const wasInteractive = el._tooltipOptions?.interactive

    // Update tooltip options
    if (binding.value !== undefined && binding.value !== null) {
      el._tooltipOptions = typeof binding.value === 'string'
        ? { title: binding.value, placement, html, trigger }
        : { 
            ...binding.value, 
            placement: placement || binding.value.placement,
            html: html || binding.value.html,
            trigger: trigger || binding.value.trigger || 'hover'
          }
    } else {
      el._tooltipOptions = { 
        title: el._originalTitle || '',
        placement,
        html,
        trigger
      }
    }

    // Handle interactive state change: if it was true and is now false, clean up hover listeners
    if (wasInteractive && !el._tooltipOptions?.interactive && el._tooltip) {
      removeTooltipHoverListeners(el)
    }

    // Update tooltip content if it exists
    if (el._tooltip && el._tooltipOptions) {
      const inner = el._tooltip.querySelector('.tooltip-inner')
      if (inner) {
        if (el._tooltipOptions.html) {
          inner.innerHTML = el._tooltipOptions.title
        } else {
          inner.textContent = el._tooltipOptions.title
        }
      }
      
      // Update placement if needed
      if (el._popper && placement) {
        el._popper.setOptions({ placement })
      }
    }
    
    // Update event listeners if trigger changed
    addTooltipListeners(el)
  },
  
  beforeUnmount(el: ExtendedHTMLElement) {
    // Ensure title is restored if it was temporarily removed
    if (el._originalTitle && !el.getAttribute('title')) {
      el.setAttribute('title', el._originalTitle)
    }
    removeTooltipListeners(el)
  }
}

export default vTooltip 